/**
 * 防抖指令
 * 用于防止频繁触发事件
 * @author 系统
 * @date 2024-01-01
 */

import type { Directive, DirectiveBinding } from 'vue'

interface DebounceElement extends HTMLElement {
  _debounceTimer?: number
  _debounceHandler?: (event: Event) => void
}

/**
 * 创建防抖函数
 * @param func 要防抖的函数
 * @param delay 延迟时间（毫秒）
 * @returns 防抖后的函数
 */
const createDebounce = (func: Function, delay: number) => {
  let timer: number
  return function (this: any, ...args: any[]) {
    clearTimeout(timer)
    timer = window.setTimeout(() => {
      func.apply(this, args)
    }, delay)
  }
}

/**
 * 防抖指令实现
 */
export const debounceDirective: Directive = {
  /**
   * 元素挂载时
   */
  mounted(el: DebounceElement, binding: DirectiveBinding) {
    const { value, arg, modifiers } = binding

    if (typeof value !== 'function') {
      console.warn('v-debounce 指令的值必须是一个函数')
      return
    }

    // 获取延迟时间，默认300ms
    const delay = parseInt(arg || '300')

    // 获取事件类型，默认click
    const eventType = modifiers.input ? 'input' :
      modifiers.change ? 'change' :
        modifiers.keyup ? 'keyup' :
          modifiers.keydown ? 'keydown' : 'click'

    // 创建防抖处理函数
    const debouncedHandler = createDebounce(value, delay)

    // 绑定事件
    el.addEventListener(eventType, debouncedHandler)

    // 保存处理函数引用，用于清理
    el._debounceHandler = debouncedHandler
  },

  /**
   * 指令值更新时
   */
  updated(el: DebounceElement, binding: DirectiveBinding) {
    const { value, arg, modifiers, oldValue } = binding

    // 如果函数没有变化，不需要重新绑定
    if (value === oldValue) {
      return
    }

    if (typeof value !== 'function') {
      console.warn('v-debounce 指令的值必须是一个函数')
      return
    }

    // 移除旧的事件监听器
    if (el._debounceHandler) {
      const eventType = modifiers.input ? 'input' :
        modifiers.change ? 'change' :
          modifiers.keyup ? 'keyup' :
            modifiers.keydown ? 'keydown' : 'click'
      el.removeEventListener(eventType, el._debounceHandler)
    }

    // 获取延迟时间，默认300ms
    const delay = parseInt(arg || '300')

    // 获取事件类型，默认click
    const eventType = modifiers.input ? 'input' :
      modifiers.change ? 'change' :
        modifiers.keyup ? 'keyup' :
          modifiers.keydown ? 'keydown' : 'click'

    // 创建新的防抖处理函数
    const debouncedHandler = createDebounce(value, delay)

    // 绑定新的事件
    el.addEventListener(eventType, debouncedHandler)

    // 更新处理函数引用
    el._debounceHandler = debouncedHandler
  },

  /**
   * 元素卸载时
   */
  unmounted(el: DebounceElement) {
    if (el._debounceHandler) {
      // 移除事件监听器
      const events = ['click', 'input', 'change', 'keyup', 'keydown']
      events.forEach(eventType => {
        el.removeEventListener(eventType, el._debounceHandler!)
      })

      // 清理引用
      delete el._debounceHandler
    }

    // 清理定时器
    if (el._debounceTimer) {
      clearTimeout(el._debounceTimer)
      delete el._debounceTimer
    }
  }
}
